import React, {
  createContext,
  useState,
  useContext,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { notificationRegistry }       from '../../notifications/registry/notification-registry';
import { NotificationService }         from '../../notifications/service/notification.service';
import { NotificationQueueManager }    from '../../notifications/service/notification-queue.manager';

const NotificationContext = createContext(null);

export const useNotificationContext = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotificationContext debe ser usado dentro de NotificationProvider');
  }
  return ctx;
};

export const NotificationProvider = ({ children }) => {
  const [state, setState] = useState({ visible: [], queuedCount: 0 });

  const queueRef   = useRef(null);
  const serviceRef = useRef(null);

  // Inicialización lazy — estable entre renders
  if (!queueRef.current) {
    queueRef.current = new NotificationQueueManager((s) => setState(s));
  }

  const addNotification = useCallback((data) => {
    queueRef.current.add(data);
  }, []);

  const removeNotification = useCallback((id) => {
    queueRef.current.remove(id);
  }, []);

  const clearNotifications = useCallback(() => {
    queueRef.current.clear();
  }, []);

  useEffect(() => {
    serviceRef.current = new NotificationService(notificationRegistry, addNotification);
    serviceRef.current.start();
    return () => serviceRef.current?.stop();
  }, [addNotification]);

  const value = useMemo(() => ({
    notifications:    state.visible,   // alias para compatibilidad con consumidores existentes
    queuedCount:      state.queuedCount,
    addNotification,
    removeNotification,
    clearNotifications,
  }), [state, addNotification, removeNotification, clearNotifications]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
